import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import axios from 'axios';
import ResiDeskIconWhiteOrange from '../../Assets/resideskIconWhiteOrange-01.svg';
import {
  Container,
  ContentWrapper,
  Logo,
  LogoContainer,
  LogoText,
  Title,
  Subtitle,
  Form,
  InputWrapper,
  Input,
  TogglePassword,
  Button,
  ErrorMessage,
  LoadingMessage,
  Rule,
  InfoContainer,
  ValidationContainer
} from './styles';

const PasswordReset = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isValidating, setIsValidating] = useState(true);
  const [validations, setValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    match: false
  });
  const [showValidations, setShowValidations] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { token } = queryString.parse(location.search);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const { data } = await axios.get(`/api/reset-password/validate-reset-token/${token}`);
        if (data.valid) {
          setUserInfo(data.user);
        } else {
          setError('Invalid reset link');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid reset link');
      } finally {
        setIsValidating(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setIsValidating(false);
      setError('No reset token provided');
    }
  }, [token]);

  // Update validations whenever password or confirmPassword changes
  useEffect(() => {
    setValidations({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      match: password && confirmPassword ? password === confirmPassword : false
    });
  }, [password, confirmPassword]);

  const handlePasswordBlur = () => {
    setShowValidations(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await axios.post('/api/reset-password/reset-password', {
        token,
        newPassword: password
      });

      history.push('/onboarding', { 
        message: 'Password reset successful. Please log in with your new password.'
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <Container>
        <ContentWrapper>
          <LogoContainer>
            <Logo src={ResiDeskIconWhiteOrange} alt="ResiDesk Logo" />
            <LogoText>ResiDesk</LogoText>
          </LogoContainer>
          <LoadingMessage>Validating reset link...</LoadingMessage>
        </ContentWrapper>
      </Container>
    );
  }

  // Show error if token is invalid or missing
  if (!token || error) {
    return (
      <Container>
        <ContentWrapper>
          <LogoContainer>
            <Logo src={ResiDeskIconWhiteOrange} alt="ResiDesk Logo" />
            <LogoText>ResiDesk</LogoText>
          </LogoContainer>
          <ErrorMessage>
            {error || 'Invalid reset link. Please request a new password reset.'}
          </ErrorMessage>
        </ContentWrapper>
      </Container>
    );
  }

  // Show error if no user info (shouldn't happen if token is valid)
  if (!userInfo) {
    return (
      <Container>
        <ContentWrapper>
          <LogoContainer>
            <Logo src={ResiDeskIconWhiteOrange} alt="ResiDesk Logo" />
            <LogoText>ResiDesk</LogoText>
          </LogoContainer>
          <ErrorMessage>Unable to verify user information.</ErrorMessage>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <LogoContainer>
          <Logo src={ResiDeskIconWhiteOrange} alt="ResiDesk Logo" />
          <LogoText>ResiDesk</LogoText>
        </LogoContainer>
        <InfoContainer>
          <Title>Reset Password</Title>
          <Subtitle>
            {userInfo.email}
          </Subtitle>
        </InfoContainer>
        <Form as="form" onSubmit={handleSubmit}>
          <InputWrapper>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={handlePasswordBlur}
              disabled={isLoading}
            />
            <TogglePassword
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Hide" : "Show"}
            </TogglePassword>
          </InputWrapper>
          <InputWrapper>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
            <TogglePassword
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </TogglePassword>
          </InputWrapper>
          <Button 
            type="submit" 
            disabled={isLoading || !Object.values(validations).every(Boolean)}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Form>
        {showValidations && (
          <ValidationContainer>
            <div><b>Please ensure your password meets the following requirements:</b></div>
            <Rule isValid={validations.length}>At least 8 characters long</Rule>
            <Rule isValid={validations.uppercase}>Contains an uppercase letter</Rule>
            <Rule isValid={validations.lowercase}>Contains a lowercase letter</Rule>
            <Rule isValid={validations.number}>Contains a number</Rule>
            {password && confirmPassword && (
              <Rule isValid={validations.match}>Passwords match</Rule>
            )}
          </ValidationContainer>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default PasswordReset; 